import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookMarked, FileSpreadsheet, PenLine } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Alert } from '@/components/ui/Alert';
import { standardReportsService } from '@/services/standardReports.service';
import { customReportsApi } from '@/services/customReports.service';
import { getErrorMessage } from '@/services/apiClient';
import type { StandardReportDescriptor } from '@/types/standardReports.types';

/**
 * The one place a report is ordered — pre-built or custom.
 *
 * WHAT THIS REPLACES. This route rendered a grid of six hardcoded tiles that
 * duplicated the sidebar, and no nav entry pointed at it: the only way to see it
 * was to start the report builder and cancel, because the builder hard-coded
 * navigate('/reports'). It was reachable exclusively by backing out of
 * something. It now has a nav home and real content.
 *
 * WHY PRE-BUILT COMES FIRST AND WITHOUT A FORK. Picking a ready-made report is
 * the common case, and asking "pre-built or custom?" before showing anything
 * makes people choose before they know what the pre-built ones contain. The
 * catalog is listed, and building your own sits under it as the escape hatch.
 *
 * The catalog is entirely server-driven — the allow-list and the caller's user
 * level are applied there. Nothing about which reports exist is hardcoded here,
 * which is what stopped the previous tile grid from drifting out of agreement
 * with reality.
 */
export default function NewReportPage() {
  const navigate = useNavigate();

  const [reports, setReports] = useState<StandardReportDescriptor[]>([]);
  const [presetCount, setPresetCount] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    standardReportsService
      .catalog()
      .then((data) => {
        if (!cancelled) setReports(data);
      })
      .catch((e) => {
        if (!cancelled) setError(getErrorMessage(e, 'Could not load the report catalog.'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    // Best-effort: the count sharpens the preset card's subtitle, and its
    // absence must not block ordering a report. A failure here is silent on
    // purpose.
    customReportsApi.presets
      .list()
      .then((data) => {
        if (!cancelled) setPresetCount(Array.isArray(data) ? data.length : 0);
      })
      .catch(() => {
        if (!cancelled) setPresetCount(null);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // Grouped by the category each descriptor declares, so a new category appears
  // here without a change to this file.
  const grouped = useMemo(() => {
    const map = new Map<string, StandardReportDescriptor[]>();

    for (const report of reports) {
      const list = map.get(report.category) ?? [];
      list.push(report);
      map.set(report.category, list);
    }

    return Array.from(map.entries());
  }, [reports]);

  const presetSubtitle =
    presetCount === null
      ? 'Reuse a saved definition'
      : presetCount === 1
        ? '1 saved preset'
        : `${presetCount} saved presets`;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="shrink-0 p-6 pb-0">
        <PageHeader variant="compact" title="New Report" />
        {error && <Alert variant="error">{error}</Alert>}
      </div>

      <div className="min-h-0 flex-1 overflow-auto p-6 pt-0">
        {loading && <div className="text-sm text-slate-500">Loading reports…</div>}

        {!loading && !error && reports.length === 0 && (
          <Alert variant="info">
            No pre-built reports are available on this account. You can still build your own below.
          </Alert>
        )}

        {grouped.map(([category, items]) => (
          <section key={category} className="mb-8">
            <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
              {grouped.length > 1 ? category : 'Pre-built'}
            </h2>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {items.map((report) => (
                <ReportCard
                  key={report.key}
                  icon={<FileSpreadsheet className="h-5 w-5" />}
                  title={report.title}
                  subtitle={report.summary}
                  // A report with its own page names it; every other one opens in the runner.
                  onClick={() => navigate(report.route ?? `/reports/standard/${report.key}`)}
                />
              ))}
            </div>
          </section>
        ))}

        <section className="mb-4">
          <h2 className="mb-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
            Build your own
          </h2>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            <ReportCard
              icon={<PenLine className="h-5 w-5" />}
              title="Start from scratch"
              subtitle="Choose the data, columns, filters and schedule yourself"
              onClick={() => navigate('/reports/builder/new')}
            />
            <ReportCard
              icon={<BookMarked className="h-5 w-5" />}
              title="Start from a preset"
              subtitle={presetSubtitle}
              onClick={() => navigate('/reports/presets')}
            />
          </div>
        </section>
      </div>
    </div>
  );
}

function ReportCard({
  icon,
  title,
  subtitle,
  onClick,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="group rounded-xl border border-slate-200 bg-white p-5 text-left shadow-sm transition-all hover:border-slate-400 hover:shadow-md"
    >
      <div className="mb-3 flex items-center gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-600 transition-colors group-hover:bg-slate-200">
          {icon}
        </div>
        <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
      </div>
      <p className="text-xs leading-relaxed text-slate-500">{subtitle}</p>
    </button>
  );
}
